import React from 'react';
import PropTypes from 'prop-types';
import * as styles from './styles/styles.module.scss';

import { wrapEventHandler } from '../../utils/handlers';

const priorityTags = [
  'closed',
  'fee',
  'dogs',
  'dogs-no',
  'dogs-leash'
];

const BulkTrailEditLeftPanel = ({
  activeIndex,
  addAlert,
  addRemoveAlertOnSelectedTrails,
  addRemoveTag,
  alertOnTrails,
  alerts,
  changes,
  deleteAlert,
  editAlert,
  highlightedTag,
  tabs,
  selectAlertsTrails,
  selectedTrails,
  tagClick,
  trailHasTag,
  trails,
  trailTags
}) => {
  const getSelectedTrailIds = () => {
    return Object.keys(selectedTrails).map(id => +id).filter(trailId => selectedTrails[trailId]);
  };

  const offerAddSub = tagUid => {
    // Offer add button unless all selected trails:
    // - have it, and there it not a "sub" set for it
    // - has an "add" set for it
    // Reverse for remove button
    let count = 0;
    let offerAdd = false;
    let offerSub = false;
    trails.forEach(trail => {
      if (selectedTrails[trail.id]) {
        const trailHasThisTag = trailHasTag(trail, tagUid);
        const hasAdd = changes[trail.id] && changes[trail.id].tags && changes[trail.id].tags.add.includes(tagUid);
        const hasSub = changes[trail.id] && changes[trail.id].tags && changes[trail.id].tags.sub.includes(tagUid);
        if ((!trailHasThisTag && !hasAdd) || (trailHasThisTag && hasSub)) {
          offerAdd = true;
        }
        if ((trailHasThisTag && !hasSub) || (!trailHasThisTag && hasAdd)) {
          count++;
          offerSub = true;
        }
      }
    });
    return { count, offerAdd, offerSub };
  };

  const renderTag = tag => {
    const { count, offerAdd, offerSub } = offerAddSub(tag.uid);
    const classes = `small rounded active ${tag === highlightedTag ? styles.highlighted : ''}`;
    const tagSpan = (
      <span className="tag">
        <span className={classes} onClick={wrapEventHandler(()=>tagClick(tag))}>{`${tag.name}${count ? ` (${count})` : ''}`}</span>
      </span>
    );
    const addButton = offerAdd ?
      <button onClick={wrapEventHandler(()=>addRemoveTag(tag, true))}>Add</button> : null;
    const removeButton = offerSub ?
      <button onClick={wrapEventHandler(()=>addRemoveTag(tag, false))}>Remove</button> : null;
    return (
      <tr key={tag.uid}>
        <td>{tagSpan}</td>
        <td>{addButton}</td>
        <td>{removeButton}</td>
      </tr>
    );
  };

  const renderPriorityTags = () => {
    let tags = [];
    priorityTags.forEach(tagUid => {
      Object.values(trailTags).forEach(section => {
        section.forEach(tag => {
          if (tag.uid === tagUid) {
            tags.push(renderTag(tag));
          }
        })
      })
    });
    return tags;
  };

  const renderTagSection = (section) => {
    let tags = [];
    trailTags[section].map(tag => {
      if (priorityTags.indexOf(tag.uid) === -1) {
        tags.push(renderTag(tag));
      }
    });
    return tags;
  };

  const renderTags = () => {
    return (
      <table>
        <tbody>
          {renderPriorityTags()}
          <tr><td className={styles.divider} colSpan="3"></td></tr>
          {renderTagSection("activities")}
          <tr><td className={styles.divider} colSpan="3"></td></tr>
          {renderTagSection("features")}
          <tr><td className={styles.divider} colSpan="3"></td></tr>
          {renderTagSection("obstacles")}
        </tbody>
      </table>
    );
  };

  const buildAlertRow = (alert, selectedTrailIds) => {
    const onTrails = alertOnTrails(alert);
    let ifAnySelectedSpan = null;
    if (selectedTrailIds.length > 0) {
      const removeFromCount = onTrails.filter(trail => selectedTrailIds.includes(trail.id)).length;
      const addToCount = selectedTrailIds.length - removeFromCount;
      const addToSpan = (addToCount > 0 && <span>{" | "}<a onClick={
        wrapEventHandler(() => addRemoveAlertOnSelectedTrails(alert.id, true))
      }>Add to {addToCount}</a></span>);
      const removeFromSpan = (removeFromCount > 0 && <span>{" | "}<a onClick={
        wrapEventHandler(() => addRemoveAlertOnSelectedTrails(alert.id, false))
      }>Remove from {removeFromCount}</a></span>);
      ifAnySelectedSpan = (
        <span>
          {addToSpan}
          {removeFromSpan}
        </span>
      );
    }
    return (
      <p key={alert.id}>
        <b>{alert.shortName || `${alert.id}. ${alert.content}`}</b>
        {alert.type === "TrailAlert" && " (Trail Alert)"}
        <br/>
        <a onClick={wrapEventHandler(() => editAlert(alert))}>Edit</a>
        {" | "}<a onClick={wrapEventHandler(() => deleteAlert(alert))}>Delete</a>
        {" | "}<a onClick={wrapEventHandler(() => selectAlertsTrails(alert))}>On {onTrails.length}</a>
        {ifAnySelectedSpan}
      </p>
    )
  };

  const renderAlerts = () => {
    const selectedTrailIds = getSelectedTrailIds();
    const areaAlertsRows = alerts
        .filter(alert => alert.type === "AreaAlert")
        .map(alert => buildAlertRow(alert, selectedTrailIds));
    const trailAlertsRows = alerts
        .filter(alert => alert.type === "TrailAlert")
        .map(alert => buildAlertRow(alert, selectedTrailIds));
    return (
      <div>
        {areaAlertsRows}
        <p>
          <a onClick={wrapEventHandler(() => addAlert())}>Add Park Alert</a>
        </p>
        <hr/>
        {trailAlertsRows}
      </div>
    );
  };

  switch (tabs[activeIndex].key) {
    case "tags":
      return renderTags();
    case "alerts":
      return renderAlerts();
  }
};

BulkTrailEditLeftPanel.propTypes = {
  activeIndex: PropTypes.number.isRequired,
  addAlert: PropTypes.func.isRequired,
  addRemoveAlertOnSelectedTrails: PropTypes.func.isRequired,
  addRemoveTag: PropTypes.func.isRequired,
  alertOnTrails: PropTypes.func.isRequired,
  alerts: PropTypes.array.isRequired,
  changes: PropTypes.object.isRequired,
  deleteAlert: PropTypes.func.isRequired,
  editAlert: PropTypes.func.isRequired,
  highlightedTag: PropTypes.object,
  tabs: PropTypes.array.isRequired,
  selectedTrails: PropTypes.object.isRequired,
  tagClick: PropTypes.func.isRequired,
  trailHasTag: PropTypes.func.isRequired,
  trails: PropTypes.array.isRequired,
  trailTags: PropTypes.object.isRequired
};

export default BulkTrailEditLeftPanel;
